import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import AdminLayout from './layouts/AdminLayout';
import TableDemo from './pages/Demo';
import { ROUTES } from './routes/path';
import { authService } from './services';
import { RoleEnum } from './types';

// Pages - to be created
const Login = React.lazy(() => import('./pages/auth/Login'));
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const LeaderBoardPlayerItems = React.lazy(() => import('./pages/leaderboard/LeaderBoardPlayerItems'));
const LeaderBoardFanClubItems = React.lazy(() => import('./pages/leaderboard/LeaderBoardFanClubItems'));
const Games = React.lazy(() => import('./pages/Games/Games'));
const CreateQuestion = React.lazy(() => import('./pages/Games/CreateQuestion'));

// const Users = React.lazy(() => import('./pages/Users'));
// const Missions = React.lazy(() => import('./pages/Missions'));
const FanClubItems = React.lazy(() => import('./pages/fanclub/FanClubItems'));
const FanClubCreate = React.lazy(() => import('./pages/fanclub/FanClubCreate'));
const FanClubEdit = React.lazy(() => import('./pages/fanclub/FanClubEdit'));
const MatchItems = React.lazy(() => import('./pages/match/MatchItems'));
const MatchCreate = React.lazy(() => import('./pages/match/MatchCreate'));
const MatchEdit = React.lazy(() => import('./pages/match/MatchEdit'));
const MatchDetail = React.lazy(() => import('./pages/match/MatchDetail'));
const MatchListOfWinner = React.lazy(() => import('./pages/match/MatchListOfWinner'));
const MatchFinished = React.lazy(() => import('./pages/match/MatchFinished'));
const TeamItems = React.lazy(() => import('./pages/team/TeamItems'));
const TeamCreate = React.lazy(() => import('./pages/team/TeamCreate'));
const TeamEdit = React.lazy(() => import('./pages/team/TeamEdit'));
const TeamDetail = React.lazy(() => import('./pages/team/TeamDetail'));
const TournamentItems = React.lazy(() => import('./pages/tournament/TournamentItems'));
const TournamentCreate = React.lazy(() => import('./pages/tournament/TournamentCreate'));
const TournamentEdit = React.lazy(() => import('./pages/tournament/TournamentEdit'));
const TournamentDetail = React.lazy(() => import('./pages/tournament/TournamentDetail'));
const UserManagementItems = React.lazy(() => import('./pages/setting/UserManagementItems'));
const FunctionItems = React.lazy(() => import('./pages/setting/function/FunctionItems'));
const FeatureItems = React.lazy(() => import('./pages/setting/function/FeatureItems'));
const CacheSystem = React.lazy(() => import('./pages/setting/CacheSystem'));
const UserProfile = React.lazy(() => import('./pages/user/UserProfile'));
const RewardList = React.lazy(() => import('./pages/reward/RewardList'));
const RewardEdit = React.lazy(() => import('./pages/reward/RewardEdit'));
const RewardCreate = React.lazy(() => import('./pages/reward/RewardCreate'));
const PackageList = React.lazy(() => import('./pages/package/PackageList'));
const PackageEdit = React.lazy(() => import('./pages/package/PackageEdit'));
const PackageCreate = React.lazy(() => import('./pages/package/PackageCreate'));
const BackgroundList = React.lazy(() => import('./pages/background/BackgroundList'));
const BackgroundEdit = React.lazy(() => import('./pages/background/BackgroundEdit'));
const BackgroundCreate = React.lazy(() => import('./pages/background/BackgroundCreate'));
const ShareLinkList = React.lazy(() => import('./pages/share-link/ShareLinkList'));
const ShareLinkEdit = React.lazy(() => import('./pages/share-link/ShareLinkEdit'));
const ShareLinkCreate = React.lazy(() => import('./pages/share-link/ShareLinkCreate'));
const LogReward = React.lazy(() => import('./pages/logs/LogReward'));
const LogQuiz = React.lazy(() => import('./pages/logs/LogQuiz'));
const LogPrediction = React.lazy(() => import('./pages/logs/LogPrediction'));
const LogPlayerTurns = React.lazy(() => import('./pages/logs/LogPlayerTurns'));
const LogPlayerPoint = React.lazy(() => import('./pages/logs/LogPlayerPoint'));
const RulesList = React.lazy(() => import('./pages/rules/RulesList'));
const RulesEdit = React.lazy(() => import('./pages/rules/RulesEdit'));
const RulesCreate = React.lazy(() => import('./pages/rules/RulesCreate'));

// Protected route component
const ProtectedRoute = ({ children, allowed = RoleEnum.Dashboard }: { children: React.ReactNode; allowed?: RoleEnum }) => {
  const isAllowed = authService.isAllowedPermission(allowed);

  if (!authService.isAuthenticated()) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (authService.isAuthenticated() && !isAllowed) {
    return <Navigate to={ROUTES.FORBIDDEN} replace />;
  }

  return <>{children}</>;
};

// Page not found route
const PageNotFound = () => {
  return (
    <AdminLayout title="Not Found">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            Không tìm thấy trang
          </Typography>
        </Stack>
      </Box>
    </AdminLayout>
  );
};

const PageForbidden = () => {
  return (
    <AdminLayout title="Forbidden">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            Bạn không có quyền truy cập trang này
          </Typography>
        </Stack>
      </Box>
    </AdminLayout>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <React.Suspense fallback={<LinearProgress color="secondary" />}>
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute allowed={RoleEnum.Dashboard}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.LEADER_BOARD_PLAYER.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LeaderBoardPlayerItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LEADER_BOARD_FANCLUB.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LeaderBoardFanClubItems />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.FAN_CLUB.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FanClubItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.FAN_CLUB.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FanClubCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.FAN_CLUB.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FanClubEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.MATCH.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchItems />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.MATCH.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.MATCH.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.MATCH.DETAIL()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MATCH.LIST_OF_WINNER()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchListOfWinner />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MATCH.FINISHED()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <MatchFinished />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TEAM.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TeamItems />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TEAM.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TeamCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TEAM.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TeamEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TEAM.DETAIL()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TeamDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TOURNAMENT.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TournamentItems />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TOURNAMENT.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TournamentCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TOURNAMENT.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TournamentEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.TOURNAMENT.DETAIL()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TournamentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.USER_MANAGEMENT}
              element={
                <ProtectedRoute allowed={RoleEnum.Admin}>
                  <UserManagementItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_PROFILE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.GAMES.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <Games />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.GAMES.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <CreateQuestion />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.GAMES.EDIT(':id')}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <CreateQuestion />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.FUNCTION}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FunctionItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.DEMO}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TableDemo />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.FEATURE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FeatureItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.CONFIG.SHARE_LINK.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.SHARE_LINK.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.SHARE_LINK.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.RULES.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.CONFIG.RULES.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.RULES.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.SETTING.CACHE_SYSTEM}
              element={
                <ProtectedRoute allowed={RoleEnum.Admin}>
                  <CacheSystem />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.LOG.REWARD}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogReward />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.QUIZ}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogQuiz />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.PREDICTION}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPrediction />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.PLAYER_TURNS}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPlayerTurns />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.PLAYER_POINT}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPlayerPoint />
                </ProtectedRoute>
              }
            />

            <Route path={ROUTES.NOT_FOUND} element={<PageNotFound />} />
            <Route path={ROUTES.FORBIDDEN} element={<PageForbidden />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </LocalizationProvider>
  );
}

export default App;
